import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { t } from '@/locales/strings';
import { safeWrite } from '@/utils/supabaseWrite';
import './ProgramComplete.css';

// ── ProgramComplete ─────────────────────────────────────────────────────
//
// Year 1 graduation screen. Rendered by WeekHome when
//   isYear1 && (userProgress.current_day >= 360)
// replacing the normal hero + session card + week-strip chain.
//
// Four sections on one scrollable page — no tabs, no steps:
//   1. The moment        — full-width rose gradient, big "360".
//   2. Her stats         — three beige stat cards + one line of body copy.
//   3. Paula quote       — left-bordered pale-rose blockquote.
//   4. Year 2 reveal     — dark brown panel, gold CTA, secondary text link.
//
// Animations: the "360" number and the three stat cards fade in with the
// `pc-fade-in` helper in ProgramComplete.css (translateY(20px) → 0,
// opacity 0 → 1), staggered 0.15s each via inline animation-delay.
//
// Source-of-truth note on field names:
//   The one-pager spec requested `userProgress.streak || 360` and
//   `userProgress.total || 360`. AppContext actually exposes
//   `streak_days` and `total_sessions_completed` (see
//   src/contexts/AppContext.jsx). We read the real field names here and
//   keep the same `|| 360` fallback so a freshly-loaded context that
//   defaults streak_days / total to 0 still shows the ceremonial "360"
//   instead of a jarring zero.

export default function ProgramComplete() {
  const { user, userProgress, lang, refreshProgress } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const bestStreak = userProgress?.streak_days || 360;
  const totalSessions = userProgress?.total_sessions_completed || 360;
  const movementsLearned = 7; // The 7 named movements of Year 1.

  const todayLabel = new Date().toLocaleDateString(
    lang === 'pt' ? 'pt-BR' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );

  // Merge `patch` into the bb7 localStorage blob and upsert via safeWrite.
  // Same pattern as ProgramReveal.beginDayOne — safeWrite takes the full
  // data object, not a patch, so we read → merge → write.
  async function writeProgress(patch) {
    let data = {};
    try {
      const saved = localStorage.getItem('bb7');
      if (saved) data = JSON.parse(saved);
    } catch { /* bad JSON or no storage — start from an empty blob */ }
    Object.assign(data, patch);
    await safeWrite(user?.id, data);
  }

  async function onBeginYear2() {
    if (busy) return;
    setBusy(true);
    try {
      await writeProgress({ current_year: 2, current_day: 1, current_week: 1 });
      try { await refreshProgress(); } catch { /* non-fatal */ }
      navigate('/home');
    } catch (err) {
      console.error('[ProgramComplete] begin Year 2 failed', err);
      setBusy(false);
    }
  }

  async function onStayInYear1() {
    if (busy) return;
    setBusy(true);
    try {
      await writeProgress({ current_day: 359 });
      try { await refreshProgress(); } catch { /* non-fatal */ }
      navigate('/home');
    } catch (err) {
      console.error('[ProgramComplete] stay in Year 1 failed', err);
      setBusy(false);
    }
  }

  return (
    <div className="pc-scroll">
      {/* ── Section 1 — The moment ───────────────────────────────── */}
      <section
        style={{
          background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 60%, var(--primary-fixed) 100%)',
          padding: '40px 24px',
          paddingTop: 'calc(96px + var(--safe-top, 0px))',
          textAlign: 'center',
        }}
      >
        <div
          className="pc-fade-in"
          style={{
            fontFamily: 'var(--font-title)',
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: 96,
            lineHeight: 0.95,
            color: '#fff',
            letterSpacing: '-0.03em',
            fontVariationSettings: '"opsz" 144',
            animationDelay: '0s',
          }}
        >
          360
        </div>
        <div
          style={{
            marginTop: 14,
            fontFamily: 'var(--font-ui)',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.32em',
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          {t('program_complete_days_label', lang)}
        </div>
        <div
          style={{
            marginTop: 10,
            fontFamily: 'var(--font-ui)',
            fontSize: 12,
            color: 'rgba(255,255,255,0.65)',
          }}
        >
          {todayLabel}
        </div>
      </section>

      {/* ── Section 2 — Her stats ────────────────────────────────── */}
      <section
        style={{
          background: 'var(--cream)',
          padding: '32px 24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'space-between',
          }}
        >
          {[
            { num: bestStreak, key: 'program_complete_stat_best_streak', delay: '0.15s' },
            { num: totalSessions, key: 'program_complete_stat_sessions', delay: '0.30s' },
            { num: movementsLearned, key: 'program_complete_stat_movements', delay: '0.45s' },
          ].map((stat) => (
            <div
              key={stat.key}
              className="pc-fade-in"
              style={{
                flex: 1,
                background: 'var(--beige)',
                borderRadius: 16,
                padding: '18px 10px',
                textAlign: 'center',
                animationDelay: stat.delay,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-title)',
                  fontStyle: 'italic',
                  fontWeight: 700,
                  fontSize: 32,
                  lineHeight: 1,
                  color: 'var(--dark-brown)',
                }}
              >
                {stat.num}
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'var(--warm-brown)',
                  lineHeight: 1.3,
                }}
              >
                {t(stat.key, lang)}
              </div>
            </div>
          ))}
        </div>

        <p
          style={{
            marginTop: 24,
            marginBottom: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--warm-brown)',
            textAlign: 'center',
          }}
        >
          {t('program_complete_stats_body', lang)}
        </p>
      </section>

      {/* ── Section 3 — Paula quote ──────────────────────────────── */}
      <section
        style={{
          background: 'var(--cream)',
          padding: '0 24px 32px',
        }}
      >
        <blockquote
          style={{
            borderLeft: '3px solid #ffd9e4',
            background: '#fff8fa',
            borderRadius: '0 10px 10px 0',
            padding: '14px 16px',
            margin: 0,
            fontFamily: 'var(--font-title)',
            fontStyle: 'italic',
            fontSize: 15,
            lineHeight: 1.55,
            color: 'var(--warm-brown)',
          }}
        >
          {'\u201C'}{t('program_complete_paula_quote', lang)}{'\u201D'}
        </blockquote>
      </section>

      {/* ── Section 4 — Year 2 reveal ────────────────────────────── */}
      <section
        style={{
          background: '#1C0A12',
          padding: '32px 24px',
          color: '#fff',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            color: 'var(--primary-light)',
          }}
        >
          {t('program_complete_year2_label', lang)}
        </div>

        <h2
          style={{
            margin: '12px 0 14px',
            fontFamily: 'var(--font-title)',
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: 36,
            lineHeight: 1.08,
            letterSpacing: '-0.02em',
            color: '#fff',
            fontVariationSettings: '"opsz" 96',
          }}
        >
          {t('program_complete_year2_title', lang)}
        </h2>

        <p
          style={{
            margin: '0 0 20px',
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.75)',
          }}
        >
          {t('program_complete_year2_body', lang)}
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 24,
          }}
        >
          {['program_complete_year2_pill_1', 'program_complete_year2_pill_2', 'program_complete_year2_pill_3'].map((key) => (
            <span
              key={key}
              style={{
                background: 'var(--primary)',
                color: '#fff',
                fontFamily: 'var(--font-ui)',
                fontSize: 11,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: 999,
              }}
            >
              {t(key, lang)}
            </span>
          ))}
        </div>

        <button
          type="button"
          onClick={onBeginYear2}
          disabled={busy}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: 'var(--gold, #A8003F)',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            fontFamily: 'var(--font-ui)',
            fontSize: 13,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.6 : 1,
            minHeight: 48,
            WebkitTapHighlightColor: 'transparent',
            transition: 'opacity 150ms ease',
          }}
        >
          {t('program_complete_cta_begin_year2', lang)}
        </button>

        <button
          type="button"
          onClick={onStayInYear1}
          disabled={busy}
          style={{
            display: 'block',
            width: '100%',
            marginTop: 16,
            padding: '10px 0',
            background: 'transparent',
            color: 'rgba(255,255,255,0.55)',
            border: 'none',
            fontFamily: 'var(--font-ui)',
            fontSize: 12,
            textDecoration: 'underline',
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.6 : 1,
            minHeight: 44,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('program_complete_cta_stay_year1', lang)}
        </button>
      </section>
    </div>
  );
}
